// https://github.com/tradingview/charting_library/wiki/Featuresets
// http://tradingview.github.io/featuresets.html

import { map } from '@flipster/utils/map';
import { lt } from '@flipster/utils/version';

import type { ChartingLibraryFeatureset } from '../../lib/charting_library';

type DisplayModeType = 'web' | 'webview-chart' | 'webview-fullchart';

// 기본값이 Off인데 써야 하는 값
const EnabledFeatures: ChartingLibraryFeatureset[] = [
  'hide_image_invalid_symbol',
  // 'display_legend_on_all_charts', // Display legend on all diagrams regardless of crosshair synchronization
  'dont_show_boolean_study_arguments', // Hides true/false study arguments
  'hide_last_na_study_output', // Hides last n/a study output data
  'snapshot_trading_drawings', // Includes orders/positions/executions in the screenshot
  // 'show_dom_first_time', // Shows DOM panel when a user opens the Chart for the first time
  'hide_left_toolbar_by_default', // 좌측 툴바 기본으로 닫아둠
  // 'chart_style_hilo', // Adds Hi-Lo option to chart style controls
  // 'pricescale_currency', // Displays the currency in which the instrument is traded on the price axes
  // 'pricescale_unit', // Displays the unit in which the instrument is traded on the price axes
  // 'show_spread_operators', // Shows the spread operators in the Symbol Search dialog
  // 'hide_exponentiation_spread_operator', // Hide exponentiation spread operator (^) in the Symbol Search dialog
  // 'hide_reciprocal_spread_operator', // Hide reciprocal spread operator (1/x) in the Symbol Search dialog
  // 'compare_symbol_search_spread_operators', // Shows the spread operators in the Compare Search dialog - needs to be used in conjuction to show_spread_operators
  // 'hide_resolution_in_legend', // Hide the interval (D, 2D, W, M, etc.) in the chart legend and the data window
  // 'hide_unresolved_symbols_in_legend', // Hide unresolved symbols in the chart legend and the data window
  // 'show_zoom_and_move_buttons_on_touch', // On touch device show the zoom and move buttons at the bottom of the chart
  // 'hide_price_scale_global_last_bar_value', // Hide the global last price label on price scale if last bar is outside of visible range
  'move_logo_to_main_pane', // TV 로고를 메인 창으로 이동
  // 'charting_library_debug_mode', // Enables logs
  // 'side_toolbar_in_fullscreen_mode', // This enables Drawings Toolbar in the fullscreen mode
  // 'header_in_fullscreen_mode', // Enables header widget DOM element in the fullscreen mode
  // 'disable_resolution_rebuild', // Shows bar time exactly as provided by the data feed with no adjustments.
  // 'low_density_bars', // Allows zooming in to show up to one bar in the viewport
  // 'no_min_chart_width', // Disables minimum chart width limitation
  // 'fix_left_edge', // Prevents scrolling to the left of the first historical bar
  // 'lock_visible_time_range_on_resize', // Prevents changing visible time area on chart resizing
  // 'custom_resolutions', // If enabled, there is a possibility to add custom resolutions
  // 'end_of_period_timescale_marks', // Toggles the timeline marks to display the bar's end time
  // 'study_overlay_compare_legend_option', // Applies symbol display mode (ticker/description) to overlay/compare studies in the status line
  // 'study_symbol_ticker_description', // Applies symbol display mode (ticker/description) to indicator inputs in the status line
  // 'two_character_bar_marks_labels', // Display at most two characters in bar marks. The default behaviour is to only display one character.
  // 'study_templates', // 지표 탬플릿 저장 기능
  // 'seconds_resolution', // 초단위로 보여주는 기능
  // 'tick_resolution', // Enables the support of tick resolution
  // 'secondary_series_extend_time_scale', // Enables a feature to allow an additional series to extend the time scale
  // 'always_pass_called_order_to_modify', // If a bracket order is modified, the terminal passes its parent order to modifyOrder. The featureset disables this behavior.
  // 'show_order_panel_on_start', // Order Panel is visible when the chart opens
  // 'chart_hide_close_position_button', // Hide the close button for position
  // 'chart_hide_close_order_button', // Hide the close button for order
  // 'dom_widget', // Enables DOM widget visibility
  'create_volume_indicator_by_default',
  'saveload_separate_drawings_storage',
];

// 기본 값이 On인데 안쓸 경우만 주석 해제
// '앱에선 disable'이라 되어있는데, 주석이 해제되어있다? 잘못한거다
const DisabledFeatures: ChartingLibraryFeatureset[] = [
  'header_widget',
  // 'header_widget_dom_node', // Disabling this feature hides the header widget DOM element
  'symbol_search_hot_key', // 핫키로 검색, 굳이 쓸 일이 없다
  'header_symbol_search', // 헤더 심볼검색창
  // 'header_resolutions', // [분봉 단위 선택 버튼], 1시간, 1일 등
  // 'header_chart_type', // [차트 형태 선택 기능] 버튼
  'header_settings', // 우상단 설정 버튼
  // 'header_indicators', // [지표 다이얼로그 기능] => 앱에선 disable
  'header_compare',
  'header_undo_redo',
  // 'header_screenshot', => 앱에선 disable
  'header_fullscreen_button', // => 자체 제작한 전체화면으로 갈음함
  'border_around_the_chart',
  'header_saveload', // 헤더 저장/로드
  // 'left_toolbar', 좌측 툴바 => 앱에선 disable
  'control_bar', // 하단의 좌우 움직이고 확대하는 버튼 뭉치
  // 'timeframes_toolbar', // 하단의 날짜 범위 선택하는 툴바
  // 'legend_widget', // 좌상단에 BTCUSD 등등등 => 앱에서는 disable
  // 'object_tree_legend_mode', // Display object tree button in the legend at a small width
  // 'edit_buttons_in_legend', // legend에 있는 지저분한 회색 버튼
  // 'show_hide_button_in_legend', // 가리기 버튼
  // 'format_button_in_legend', // 설정 버튼
  // 'study_buttons_in_legend', // ?
  // 'delete_button_in_legend', // 삭제 버튼
  'context_menus', // 우클릭 메뉴 끔
  // 'pane_context_menu', // 차트 영역 클릭시.
  // 'scales_context_menu', // 축 클릭시
  // 'legend_context_menu', // 범례에 펼침 버튼, 더럽게 생겼는데 요긴하다
  'main_series_scale_menu', // 좌하단 설정 버튼
  // 'chart_crosshair_menu',
  // 'display_market_status', // 로딩 인디케이터
  // 'remove_library_container_border',  // ?
  // 'property_pages', // Disables all property pages
  'show_interval_dialog_on_key_press',
  // 'show_chart_property_page', // Turning this feature off disables Properties
  // 'chart_property_page_scales',
  // 'chart_property_page_trading', // This feature is for the Trading Terminal only
  // 'chart_property_page_right_margin_editor', // Shows the right margin editor in the setting dialog
  // 'countdown', // Displays a countdown label on a price scale
  // 'symbol_info', // Enables the symbol info dialog
  // 'timezone_menu', // 하단 시간대 변경 기능
  // 'source_selection_markers', // Disables selection markers for series and indicators
  'go_to_date', // 하단 특정 날짜로 이동 버튼
  // 'adaptive_logo', // 작은 디바이스에서 TV 로고 제거
  // 'scales_date_format', // Displays Date Format selector in Chart Settings
  // 'popup_hints', // Displays popup hints about possible mouse/shortcuts/UI actions
  // 'save_shortcut', // Enables the save shortcut
  // 'show_right_widgets_panel_by_default', // Opens right widget toolbar on first launch
  // 'show_object_tree', // Shows the object tree button in the left or right panel depending on the product and configuration
  // 'hide_main_series_symbol_from_indicator_legend', // Hide the optional symbol input value from the indicator's legend if 'Main chart symbol' option is selected
  // 'use_localstorage_for_settings',
  // 'items_favoriting',
  'save_chart_properties_to_local_storage', // Can be disabled to forbid storing chart properties to the localstorage while allowing to save other properties. The other properties are favorites in the Charting Library and Watchlist symbols and some panels states in the Trading Terminal.
  'volume_force_overlay', // 거래량을 메인 화면에 겹쳐 보여주기
  // 'right_bar_stays_on_scroll', // Determines the behavior of Zoom feature: bar under the mouse cursor stays in the same place if this feature is disabled
  // 'constraint_dialogs_movement', // Keeps the dialogs within the chart
  // 'chart_scroll', // Allows chart scrolling
  // 'chart_zoom', // Allows chart zooming
  // 'horz_touch_drag_scroll', // If enabled, the chart handles horizontal pointer movements on touch screens. In this case the webpage is not scrolled. If disabled, the webpage is scrolled instead. Keep in mind that if the user starts scrolling the chart vertically or horizontally, scrolling is continued in any direction until the user releases the finger.
  // 'vert_touch_drag_scroll', // If enabled, the chart handles vertical pointer movements on touch screens. In this case the webpage is not scrolled. If disabled, the webpage is scrolled instead. Keep in mind that if the user starts scrolling the chart vertically or horizontally, scrolling is continued in any direction until the user releases the finger.
  // 'mouse_wheel_scroll', // If enabled, chart scrolling with horizontal mouse wheel is enabled.
  // 'pressed_mouse_move_scroll', // If enabled, chart scrolling with left mouse button pressed is allowed.
  // 'mouse_wheel_scale', // If enabled, series scaling with a mouse wheel is enabled.
  // 'pinch_scale', // If enabled, series scaling with pinch/zoom gestures (this option is supported on touch devices) is enabled.
  // 'axis_pressed_mouse_move_scale', // If enabled, axis scaling with left mouse button pressed is allowed.
  // 'uppercase_instrument_names', // Disabling this feature allows a user to enter case-sensitive symbols
  // 'shift_visible_range_on_new_bar', // If disabled, adding a new bar zooms out the chart preserving the first visible point. Otherwise the chart is scrolled one point to the left when a new bar comes.
  // 'cropped_tick_marks', // If disabled, partially visible price labels on price axis will be hidden
  // 'auto_enable_symbol_labels', // Displays Symbol Name Label when comparing Symbols
  // 'insert_indicator_dialog_shortcut', // Enables insert indicator dialog shortcut (/)
  // 'datasource_copypaste', // Enables copying of drawings and studies
  // 'support_multicharts', // Enables context menu actions (Clone, Sync) related to Multiple Chart Layout
  // 'header_layouttoggle', // Shows the Select Layout button in the header
  // 'add_to_watchlist', // Enables "Add symbol to Watchlist" item in the menu
  // 'open_account_manager', // Keeps the Account Manager opened by default
  // 'trading_notifications', // Shows trading notifications on the chart
  // 'multiple_watchlists', // Enables creating of multiple watchlists
  // 'show_trading_notifications_history', // Enables the Notifications Log tab in the bottom panel
  // 'drawing_templates', // Enables Drawing Templates on Drawing toolbar
  // 'trading_account_manager', // Shows the Account Manager Widget
  // 'right_toolbar', // Shows the right buttons toolbar
  // 'order_panel', // Shows the Order Panel
  // 'order_info', // Shows the Order info section in the Order dialog
  // 'buy_sell_buttons', // Shows the Buy/Sell Buttons in Legend
  // 'broker_button', // Shows the Broker Button in Legend
  // 'order_panel_close_button', // Shows close Order Panel button
  // 'order_panel_undock', // Shows the Undock button in the Order Panel Settings
  // 'watchlist_import_export', // Enables watchlist export and import,
  'header_quick_search',
  'display_market_status',
];

const WebviewChartDisabledFeatures: Set<ChartingLibraryFeatureset> = new Set([
  'header_indicators', // 상단 인디케이터 버튼
  'legend_context_menu', // 범례 펼침 버튼
  // 'legend_widget', // 상단 종목명과 기타등등
  'left_toolbar', // 좌측 툴바
  'create_volume_indicator_by_default',
]);

const WebviewFullchartDisabledFeatures: Set<ChartingLibraryFeatureset> = new Set([
  'header_chart_type', // 상단 차트 모양 선택 버튼
  'header_screenshot', // 상단 스크린샷 버튼
]);

const WebviewChartDisabledFeaturesBefore303: Set<ChartingLibraryFeatureset> = new Set(['vert_touch_drag_scroll']);

const VerticalScrollDisabledFeatures: Set<ChartingLibraryFeatureset> = new Set(['vert_touch_drag_scroll']);

export const getEnabledFeatures = (
  displayMode: DisplayModeType,
  device?: 'Android' | 'iOS' | 'unknown',
  version?: string,
  featureFlags?: Record<string, boolean>,
): ChartingLibraryFeatureset[] => {
  switch (displayMode) {
    case 'webview-chart': {
      let features = EnabledFeatures.filter((f) => {
        const enabled = !(WebviewChartDisabledFeatures.has(f) || WebviewFullchartDisabledFeatures.has(f));

        if (device === 'iOS' || map(version, (version) => lt(version, '3.3'))) {
          return enabled && !WebviewChartDisabledFeaturesBefore303.has(f);
        }
        return enabled;
      });
      if (featureFlags?.disableVerticalScroll) {
        features = features.filter((f) => !VerticalScrollDisabledFeatures.has(f));
      }
      return features;
    }
    case 'webview-fullchart': {
      let features = EnabledFeatures.filter((f) => !WebviewFullchartDisabledFeatures.has(f));
      if (featureFlags?.disableVerticalScroll) {
        features = features.filter((f) => !VerticalScrollDisabledFeatures.has(f));
      }
      return features;
    }
    case 'web':
      return EnabledFeatures;
    default:
      return EnabledFeatures;
  }
};

export const getDisabledFeatures = (
  displayMode: DisplayModeType,
  device?: 'Android' | 'iOS' | 'unknown',
  version?: string,
  featureFlags?: Record<string, boolean>,
) => {
  switch (displayMode) {
    case 'webview-chart': {
      const features = [...DisabledFeatures, ...WebviewChartDisabledFeatures, ...WebviewFullchartDisabledFeatures];

      if (device === 'iOS' || map(version, (version) => lt(version, '3.3'))) {
        features.push(...WebviewChartDisabledFeaturesBefore303);
      }
      if (featureFlags?.disableVerticalScroll) {
        features.push(...VerticalScrollDisabledFeatures);
      }
      return features;
    }
    case 'webview-fullchart': {
      const features = [...DisabledFeatures, ...WebviewFullchartDisabledFeatures];
      if (featureFlags?.disableVerticalScroll) {
        features.push(...VerticalScrollDisabledFeatures);
      }
      return features;
    }
    case 'web':
      return DisabledFeatures;
    default:
      return DisabledFeatures;
  }
};
